import { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon, Clock as ClockIcon, ArrowRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { useLanguage } from "@/lib/LanguageContext";
import { trackWhatsAppClick, captureLead, trackFormStart } from "@/lib/analytics";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import WishlistButton from "@/components/WishlistButton";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Map, MapMarker, MapRoute } from "@/components/ui/map";

interface TourItineraryDay {
  day: number;
  title: string;
  description: string;
  activities: string[];
  accommodation?: string;
  meals?: string;
}

interface TourPageProps {
  title: string;
  duration: string;
  price: string;
  image: string;
  description: string;
  itinerary: TourItineraryDay[];
  inclusions: string[];
  exclusions: string[];
  tourId: string;
  mapData?: {
    center: [number, number];
    zoom: number;
    points: { coordinates: [number, number]; label: string; variant?: 'park' | 'city' | 'airport' | 'default'; description?: string }[];
    route?: [number, number][];
  };
}

interface RelatedTour {
  title: string;
  duration: string;
  image: string;
  href: string;
  description?: string;
}

export default function TourPage({
  title,
  duration,
  price,
  image,
  description,
  itinerary,
  inclusions,
  exclusions,
  tourId,
  mapData,
}: TourPageProps) {
  const [date, setDate] = useState<Date>();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [travelers, setTravelers] = useState<number | undefined>(undefined);
  const [message, setMessage] = useState("");
  const { toast } = useToast();
  const { t } = useLanguage();

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const web3formsPayload = {
        access_key: "ee06da99-1582-4a82-80e6-e7196b072777",
        subject: `New Tour Booking Request: ${title}`,
        from_name: data.name,
        email: data.email,
        phone: data.phone || "Not provided",
        tour_name: title,
        date_requested: date ? date.toLocaleDateString() : new Date().toLocaleDateString(),
        number_of_guests: data.participants,
        additional_details: data.message || "None",
      };

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(web3formsPayload),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to submit booking");
      }

      return json;
    },
    onSuccess: () => {
      toast({
        title: "Booking Request Sent!",
        description: "We've received your request and will contact you shortly to confirm your safari tour.",
      });
      // Reset form
      setName("");
      setEmail("");
      setPhone("");
      setDate(undefined);
      setTravelers(undefined);
      setMessage("");
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Submission Error",
        description: error.message || "Something went wrong. Please try again.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !phone || !date) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields.",
      });
      return;
    }

    mutation.mutate({
      name,
      email,
      phone,
      participants: travelers,
      message
    });
  };

  const getRelatedTours = (tourIdParam: string): RelatedTour[] => {
    const country = tourIdParam.split('/')[0];

    // Define related tours based on country
    const toursByCountry: Record<string, RelatedTour[]> = {
      kenya: [
        {
          title: "3 Days Maasai Mara National Reserve Safari",
          duration: "3 Days / 2 Nights",
          image: "/images/masai-mara-migration.jpg",
          description: "Experience the world-famous Maasai Mara and its diverse wildlife in this short but exciting safari.",
          href: "/tours/kenya/maasai-mara-3-day"
        },
        {
          title: "3 Days Masai Mara Road Safari",
          duration: "3 Days / 2 Nights",
          image: "/images/masai-mara-migration.jpg",
          description: "Travel by road to the Maasai Mara for an authentic safari experience with game drives and cultural interactions.",
          href: "/tours/kenya/masai-mara-road-3-day"
        },
        {
          title: "5 Days Lake Nakuru & Maasai Mara Safari",
          duration: "5 Days / 4 Nights",
          image: "/images/masai-mara-migration.jpg",
          description: "Combine the flamingo-filled Lake Nakuru with the wildlife-rich plains of the Maasai Mara.",
          href: "/tours/kenya/nakuru-maasai-mara-5-day"
        }
      ],
      rwanda: [
        {
          title: "10 Days Rwanda Safari",
          duration: "10 Days / 9 Nights",
          image: "/images/gorilla-trekking.jpg",
          description: "Explore Rwanda's national parks and experience gorilla trekking in an unforgettable safari adventure.",
          href: "/tours/rwanda/safari-10-day"
        },
        {
          title: "4 Days Akagera National Park Safari",
          duration: "4 Days / 3 Nights",
          image: "/images/serengeti-zebras.jpg",
          description: "Discover Rwanda's only savannah park with its diverse wildlife and beautiful lakes.",
          href: "/tours/rwanda/akagera-4-day"
        },
        {
          title: "6 Days Nyungwe Forest Trek",
          duration: "6 Days / 5 Nights",
          image: "/gorilla.webp",
          description: "Trek through ancient rainforest in search of chimpanzees and rare primates.",
          href: "/tours/rwanda/nyungwe-6-day"
        }
      ],
      tanzania: [
        {
          title: "8 Days Wildlife Tanzania Safari",
          duration: "8 Days / 7 Nights",
          image: "/images/serengeti-zebras.jpg",
          description: "Experience the Serengeti's Great Migration and explore iconic national parks.",
          href: "/tours/tanzania/wildlife-8-day"
        },
        {
          title: "6 Days Safari to Tanzania",
          duration: "6 Days / 5 Nights",
          image: "/images/masai-mara-migration.jpg",
          description: "Visit the Ngorongoro Crater and witness its incredible density of wildlife.",
          href: "/tours/tanzania/safari-6-day"
        },
        {
          title: "10 Days Tanzania & Zanzibar",
          duration: "10 Days / 9 Nights",
          image: "/images/giraffe.JPG",
          description: "Combine wildlife safari with relaxing beach time in the exotic Zanzibar archipelago.",
          href: "/tours/tanzania/zanzibar-10-day"
        }
      ],
      uganda: [
        {
          title: "3-Day Murchison Falls Adventure Safari",
          duration: "3 Days / 2 Nights",
          image: "/images/murchison-falls.jpg",
          description: "Experience the world's most powerful waterfall and abundant wildlife in Uganda's largest park.",
          href: "/tours/uganda/murchison-falls-3-day"
        },
        {
          title: "5 Days Wildlife Safari",
          duration: "5 Days / 4 Nights",
          image: "/Uganda-Wildlife-Safari.png",
          description: "A perfect safari through Uganda's top national parks with diverse landscapes and wildlife.",
          href: "/tours/uganda/wildlife-5-day"
        },
        {
          title: "5-Day Bwindi Gorilla Safari",
          duration: "5 Days / 4 Nights",
          image: "/gorilla.webp",
          description: "Trek through the Impenetrable Forest to encounter endangered mountain gorillas in their natural habitat.",
          href: "/tours/uganda/bwindi-gorilla-5-day"
        },
        {
          title: "8-Day Primates & Wildlife Safari",
          duration: "8 Days / 7 Nights",
          image: "/images/gorilla-trekking.jpg",
          description: "Track chimpanzees and encounter diverse primates across Uganda's rainforests and savannahs.",
          href: "/tours/uganda/primates-wildlife-8-day"
        }
      ]
    };

    // Get tours for the current country and filter out the current tour
    return (toursByCountry[country] || [])
      .filter(tour => tour.href !== `/tours/${tourIdParam}`);
  };

  const relatedTours = getRelatedTours(tourId);

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": title,
            "image": image,
            "description": description,
            "offers": {
              "@type": "Offer",
              "price": price.replace(/[^0-9.]/g, ''),
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold">{title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-2">
                <span>Duration: {duration}</span>
                <span>Price from: {price}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Tour Itinerary */}
          <div className="lg:col-span-2 space-y-6">
            <div className="aspect-[16/9] rounded-xl overflow-hidden">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">{t("tourOverview")}</h2>
              <p className="text-gray-600">{description}</p>
            </div>

            {/* Safari Route Map - Commented out for later road-realistic refinement
            {mapData && (
              <div className="bg-white rounded-lg shadow p-6 overflow-hidden">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="w-2 h-6 bg-primary rounded-full" />
                  Safari Route Map
                </h2>
                <div className="h-[400px] w-full rounded-xl overflow-hidden border border-gray-100">
                  <Map center={mapData.center} zoom={mapData.zoom}>
                    {mapData.points.map((point, idx) => (
                      <MapMarker
                        key={idx}
                        coordinates={point.coordinates}
                        label={point.label}
                        variant={point.variant}
                      />
                    ))}
                    {mapData.route && <MapRoute coordinates={mapData.route} />}
                  </Map>
                </div>
                <p className="text-sm text-gray-500 mt-4 italic text-center">
                  * Map visualization for illustrative purposes of the safari route.
                </p>
              </div>
            )}
            */}

            <div className="bg-white rounded-lg shadow">
              <h2 className="text-xl font-semibold p-6 pb-2">{t("dailyItinerary")}</h2>
              <Accordion type="single" collapsible className="px-6 pb-6">
                {itinerary.map((day) => (
                  <AccordionItem key={day.day} value={`day-${day.day}`}>
                    <AccordionTrigger className="text-left">
                      <div>
                        <span className="font-semibold">Day {day.day}:</span>{" "}
                        {day.title}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-2">
                        <p className="text-gray-600">{day.description}</p>
                        <ul className="space-y-2">
                          {day.activities.map((activity, index) => (
                            <li key={index} className="flex items-start">
                              <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 mr-2 flex-shrink-0" />
                              <span>{activity}</span>
                            </li>
                          ))}
                        </ul>
                        {(day.accommodation || day.meals) && (
                          <div className="mt-4 pt-4 border-t text-sm text-gray-500">
                            {day.accommodation && (
                              <p>Accommodation: {day.accommodation}</p>
                            )}
                            {day.meals && <p>Meals: {day.meals}</p>}
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* Right Column - Booking Form */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-xl border border-white rounded-3xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] p-8 h-fit">
              <h2 className="text-xl font-semibold mb-6 font-heading">{t("bookThisTour")}</h2>
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <Input
                    placeholder={t("yourName")}
                    value={name}
                    onFocus={() => trackFormStart(`Booking Form: ${title}`)}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-white"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    placeholder={t("emailAddress")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white"
                  />
                </div>
                <div>
                  <Input
                    placeholder={t("phoneNumber")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="bg-white"
                  />
                </div>
                <div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : <span>{t("pickDate")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Input
                    type="number"
                    placeholder={t("numberOfTravelers")}
                    min="1"
                    value={travelers === undefined ? "" : travelers}
                    onChange={(e) => setTravelers(e.target.value ? parseInt(e.target.value, 10) : undefined)}
                    required
                    className="bg-white"
                  />
                </div>
                <div>
                  <Textarea
                    placeholder={t("specialRequirements")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="bg-white"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full font-semibold shadow-md"
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? t("sending") : t("requestBooking")}
                </Button>

                <Button
                  type="button"
                  onClick={() => {
                    trackWhatsAppClick('Tour Expert Contact');
                    const msg = encodeURIComponent(`Hi, I'm viewing the "${title}" safari and would like to talk to an expert.`);
                    window.open(`https://wa.me/256780628299?text=${msg}`, '_blank');
                  }}
                  size="lg"
                  className="bg-slate-900 hover:bg-slate-800 text-white w-full font-semibold shadow-xl shadow-slate-200 transition-all hover:-translate-y-0.5 border-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-4 w-4 mr-2 fill-white"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zM223.9 438.6c-32.6 0-64.6-9.1-92.9-26.2l-6.6-4-69 18 18.4-67.4-4.3-6.9c-18.3-29.2-28-63.3-28-98.4 0-101.4 82-183.9 184-183.9 49.1 0 95.3 19.1 130 53.9 34.7 34.7 53.9 80.9 53.9 130 0 101.4-82.5 183.9-184.1 183.9zm101.5-138.4c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" /></svg>
                  Talk to a Safari Expert
                </Button>

                <WishlistButton
                  tourId={tourId}
                  tourName={title}
                  tourDescription={description}
                  tourImage={image}
                  tourHref={window.location.pathname}
                />
              </form>

              {/* Additional CTAs - simplified */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <Link href="/booking">
                  <Button
                    variant="ghost"
                    className="w-full text-primary hover:text-primary/80 hover:bg-primary/5"
                  >
                    Customize This Experience <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div >

        {/* Bottom Section - Inclusions & Exclusions */}
        < div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8" >
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 font-heading">{t("inclusions")}</h2>
            <ul className="space-y-2">
              {inclusions.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2 mr-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4 font-heading">{t("exclusions")}</h2>
            <ul className="space-y-2">
              {exclusions.map((item, index) => (
                <li key={index} className="flex items-start">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div >

        {/* Similar Tours Section */}
        {
          relatedTours.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-16 bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-6 rounded-3xl shadow-sm"
            >
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-3 font-heading">{t("similarTours")}</h2>
                <div className="w-24 h-1 bg-green-600 mx-auto rounded-full"></div>
                <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                  {t("discoverMoreTours").replace("{country}", tourId.split('/')[0].charAt(0).toUpperCase() + tourId.split('/')[0].slice(1))}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {relatedTours.map((tour) => (
                  <Link key={tour.href} href={tour.href}>
                    <motion.div
                      whileHover={{
                        scale: 1.03,
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                      }}
                      className="bg-white rounded-2xl shadow-md overflow-hidden h-full hover:shadow-xl transition-all duration-300 flex flex-col"
                    >
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={tour.image}
                          alt={tour.title}
                          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                      <div className="p-6 flex-grow">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 font-heading">{tour.title}</h3>
                        <div className="flex items-center mb-3">
                          <ClockIcon className="h-4 w-4 text-green-600 mr-2" />
                          <p className="text-gray-600">{tour.duration}</p>
                        </div>
                        {tour.description && (
                          <p className="text-gray-600 mb-4 line-clamp-2">{tour.description}</p>
                        )}
                      </div>
                      <div className="px-6 pb-6 mt-auto">
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700 text-white gap-2 shadow-sm hover:shadow transition-all"
                        >
                          {t("viewTour")}
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </div>
            </motion.div>
          )
        }
      </div >
    </div >
  );
}